/**
 * PR-D4-042 — Recent sessions card with «Работа над ошибками» launcher.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ProgressRecentSession } from '../lib/types';

const MODE_LABEL: Record<string, string> = {
    untimed: 'Untimed',
    timed: 'Timed',
    training: 'Training',
    weak_topic_training: 'Smart training',
};

const MODULE_LABEL: Record<string, string> = {
    math: 'Math',
    verbal: 'Reading & Writing',
    full_practice: 'Full practice',
};

function formatDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function formatDuration(seconds: number | null): string {
    if (seconds == null || seconds < 0) return '—';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    if (m <= 0) return `${s}s`;
    return `${m}m ${s.toString().padStart(2, '0')}s`;
}

export function DashboardRecentSessions({
    sessions,
    title = 'Recent Sessions / Test History',
    onDeleteSession,
}: {
    sessions: ProgressRecentSession[];
    title?: string;
    onDeleteSession?: (sessionId: string) => Promise<void>;
}) {
    const navigate = useNavigate();
    const [deletingSessionId, setDeletingSessionId] = useState<string | null>(null);

    const handleDelete = async (event: React.MouseEvent<HTMLButtonElement>, sessionId: string) => {
        event.stopPropagation();
        if (!onDeleteSession || deletingSessionId || !window.confirm('Удалить эту сессию из истории?')) {
            return;
        }
        setDeletingSessionId(sessionId);
        try {
            await onDeleteSession(sessionId);
        } finally {
            setDeletingSessionId(null);
        }
    };

    return (
        <section className="dashboard-sessions" aria-label="Recent sessions">
            <div className="dashboard-sessions__header">
                <h3 className="dashboard-sessions__title">{title}</h3>
            </div>
            {sessions.length === 0 ? (
                <p className="dashboard-sessions__empty">
                    No completed tests yet — finish a section to see history here.
                </p>
            ) : (
                <ul className="dashboard-sessions__list">
                    {sessions.map((session) => {
                        const scoreLabel =
                            session.score != null && session.maxScore != null
                                ? `${session.score}/${session.maxScore}`
                                : '—';
                        const misses = session.incorrectAnswersCount ?? 0;
                        return (
                            <li
                                key={session.id}
                                className="dashboard-sessions__row"
                                role="button"
                                tabIndex={0}
                                onClick={() =>
                                    navigate(`/review-session?sessionId=${encodeURIComponent(session.id)}`)
                                }
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        navigate(`/review-session?sessionId=${encodeURIComponent(session.id)}`);
                                    }
                                }}
                            >
                                <div className="dashboard-sessions__meta">
                                    <span className="dashboard-sessions__date">
                                        {formatDate(session.createdAt)}
                                    </span>
                                    <span>
                                        {MODULE_LABEL[session.moduleType] ?? session.moduleType}
                                        {' · '}
                                        {MODE_LABEL[session.mode] ?? session.mode}
                                    </span>
                                    <span className="dashboard-sessions__score">{scoreLabel}</span>
                                    <span>{formatDuration(session.durationSeconds)}</span>
                                </div>
                                {misses > 0 ? (
                                    <button
                                        type="button"
                                        className="dashboard-sessions__review"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            navigate(
                                                `/review-session?sessionId=${encodeURIComponent(session.id)}`,
                                            );
                                        }}
                                    >
                                        Работа над ошибками
                                    </button>
                                ) : session.score === session.maxScore && session.maxScore != null ? (
                                    <span className="dashboard-sessions__clean">All correct</span>
                                ) : null}
                                {onDeleteSession ? (
                                    <button
                                        type="button"
                                        className="dashboard-sessions__delete"
                                        aria-label="Удалить сессию"
                                        title="Удалить сессию"
                                        disabled={deletingSessionId === session.id}
                                        onClick={(event) => void handleDelete(event, session.id)}
                                    >
                                        {deletingSessionId === session.id ? '…' : 'Удалить'}
                                    </button>
                                ) : null}
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
}
